import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { API_BASE_URL } from "../services/api";
import {
  hasSupportSessionHint,
  subscribeToSupportSession,
} from "../services/supportApi";

const SUPPORT_REFRESH_EVENT = "support-admin-refresh";

const SUPPORT_EVENTS = [
  "support-roles-updated",
  "support-users-updated",
  "support-employees-updated",
  "support-tickets-updated",
  "support-admin-updated",
];
const SUPPORT_STREAM_PATHS = [
  "/u/support/portal",
  "/u/support/agent",
  "/u/support/admin",
  "/u/support/settings",
  "/u/admin",
];

export default function SupportServerEvents() {
  const location = useLocation();
  const [hasSession, setHasSession] = useState(() => hasSupportSessionHint());
  const events = useMemo(() => SUPPORT_EVENTS, []);
  const shouldListen = useMemo(
    () => SUPPORT_STREAM_PATHS.some((path) => location.pathname.startsWith(path)),
    [location.pathname]
  );

  useEffect(
    () =>
      subscribeToSupportSession(({ hasSession: nextHasSession }) => {
        setHasSession(Boolean(nextHasSession));
      }),
    []
  );

  useEffect(() => {
    if (!shouldListen || !hasSession) return undefined;

    const streamUrl = `${API_BASE_URL}/support/stream`;
    const source = new EventSource(streamUrl, { withCredentials: true });

    const makeHandler = (eventName) => (event) => {
      if (!event?.data) return;
      try {
        const payload = JSON.parse(event.data);
        window.dispatchEvent(
          new CustomEvent(SUPPORT_REFRESH_EVENT, {
            detail: { event: eventName, payload },
          })
        );
      } catch {
        // Ignore malformed payloads.
      }
    };

    const handlers = events.reduce((acc, eventName) => {
      acc[eventName] = makeHandler(eventName);
      source.addEventListener(eventName, acc[eventName]);
      return acc;
    }, {});

    source.onerror = () => {
      // EventSource reconnects automatically.
    };

    return () => {
      events.forEach((eventName) => {
        source.removeEventListener(eventName, handlers[eventName]);
      });
      source.close();
    };
  }, [events, hasSession, shouldListen]);

  return null;
}
